import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { CaretLeft, CaretRight, Lightning } from "@phosphor-icons/react";
import { NAV, PORTAL_BASE } from "../lib/nav";

const COLLAPSED_KEY = "tm_admin_sidebar_collapsed";

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; }
    catch { return false; }
  });
  const location = useLocation();

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const isGroupActive = (items) =>
    items.some((item) => location.pathname.startsWith(`${PORTAL_BASE}/${item.path}`));

  return (
    <aside
      className={`flex flex-col flex-shrink-0 bg-white border-r border-slate-200 transition-all duration-200 ease-in-out overflow-hidden ${
        collapsed ? "w-[60px]" : "w-60"
      }`}
      data-testid="admin-sidebar"
    >
      {/* Brand */}
      <div className="flex items-center h-14 px-4 border-b border-slate-200 shrink-0 gap-3 overflow-hidden">
        <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
          <Lightning size={13} weight="fill" className="text-white" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-bold text-sm text-slate-900 tracking-tight leading-none">TrendMing</div>
            <div className="text-xs text-slate-400 mt-0.5">Admin Portal</div>
          </div>
        )}
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 min-h-0 px-2" aria-label="Admin navigation">
        {NAV.map((entry) => {
          /* ── Top-level single item (Dashboard) ── */
          if (!entry.group) {
            const Icon = entry.icon;
            return (
              <NavLink
                key={entry.id}
                to={`${PORTAL_BASE}/${entry.path}`}
                title={collapsed ? entry.label : undefined}
                className={({ isActive }) =>
                  `flex items-center h-9 rounded-lg transition-colors ${
                    collapsed ? "justify-center px-0" : "gap-3 px-3"
                  } ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
                data-testid={`nav-${entry.id}`}
              >
                {({ isActive }) => (
                  <>
                    <Icon size={15} weight={isActive ? "fill" : "regular"} className="shrink-0" />
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{entry.label}</span>
                    )}
                  </>
                )}
              </NavLink>
            );
          }

          /* ── Grouped section ── */
          const active = isGroupActive(entry.items);
          return (
            <div key={entry.group} className="mt-5">
              {/* Group label */}
              {!collapsed ? (
                <div className="px-3 pb-1.5">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${active ? "text-slate-500" : "text-slate-400"}`}>
                    {entry.group}
                  </span>
                </div>
              ) : (
                <div className="mx-2 border-t border-slate-200 mb-1.5" />
              )}

              {entry.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.id}
                    to={`${PORTAL_BASE}/${item.path}`}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center h-9 rounded-lg transition-colors ${
                        collapsed ? "justify-center px-0" : "gap-3 px-3"
                      } ${
                        isActive
                          ? "text-blue-600 bg-blue-50"
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                      }`
                    }
                    data-testid={`nav-${item.id}`}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon size={14} weight={isActive ? "fill" : "regular"} className="shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                            {item.comingSoon && (
                              <span className="text-[10px] font-medium text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5 shrink-0">
                                Soon
                              </span>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-slate-200 shrink-0 px-2 py-2">
        <button
          onClick={toggle}
          className={`flex items-center w-full py-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors ${
            collapsed ? "justify-center px-0" : "gap-2 px-3"
          }`}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <CaretRight size={13} weight="bold" />
          ) : (
            <>
              <CaretLeft size={13} weight="bold" />
              <span className="text-xs font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
